import * as React from 'react';

import { Alert, AlertDescription, AlertTitle } from '../feedback/alert';
import { Spinner } from '../feedback/spinner';
import { Button } from '../primitives/button';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from './dialog';

export type ImagePreviewDialogProps = {
  src: string;
  alt: string;
  title: string;
  closeLabel: string;
  trigger?: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  pendingState?:
    | { status: 'loading'; label: string }
    | { status: 'error'; label: string; retryLabel: string; onRetry: () => void };
};

export function ImagePreviewDialog({
  src,
  alt,
  title,
  closeLabel,
  trigger,
  open,
  defaultOpen,
  onOpenChange,
  pendingState,
}: ImagePreviewDialogProps) {
  return (
    <Dialog open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent
        aria-describedby={undefined}
        closeLabel={closeLabel}
        closeClassName="bg-background/90 shadow-sm"
        className="h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-6xl gap-0 overflow-hidden border-0 p-0"
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <div className="flex h-full min-h-0 w-full min-w-0 items-center justify-center bg-background">
          {pendingState?.status === 'loading' ? (
            <Spinner aria-label={pendingState.label} />
          ) : pendingState?.status === 'error' ? (
            <Alert variant="destructive">
              <AlertTitle>{pendingState.label}</AlertTitle>
              <AlertDescription>
                <Button type="button" variant="outline" size="sm" onClick={pendingState.onRetry}>
                  {pendingState.retryLabel}
                </Button>
              </AlertDescription>
            </Alert>
          ) : (
            <img src={src} alt={alt} className="block max-h-full max-w-full object-contain" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
